import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  dismissVisibilityRequest,
  fetchInvitations,
  fetchVisibilityRequests,
  revokeInvitation,
  sendInvitation,
} from "../api.js";

/**
 * Who has access (1aa).
 *
 * Invite-only is the product's security position, so this is where it is
 * actually exercised: one address at a time, revocable immediately. Nothing
 * here shares a calendar — only the times somebody may request.
 */
const STATUS_LABEL: Record<string, string> = {
  invited: "Invited",
  confirmed: "Confirmed",
  revoked: "Revoked",
};

export function Invitations() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [lastLink, setLastLink] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "confirmed" | "invited" | "revoked">(
    "all",
  );

  const invitations = useQuery({ queryKey: ["invitations"], queryFn: fetchInvitations });
  const asks = useQuery({
    queryKey: ["visibility-requests"],
    queryFn: fetchVisibilityRequests,
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["invitations"] });

  const dismissAsk = useMutation({
    mutationFn: dismissVisibilityRequest,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["visibility-requests"] }),
  });

  const invite = useMutation({
    mutationFn: () => sendInvitation(email, note || undefined),
    onSuccess: (result) => {
      setEmail("");
      setNote("");
      setLastLink(result.link);
      return invalidate();
    },
  });

  const revoke = useMutation({ mutationFn: revokeInvitation, onSuccess: invalidate });

  // Accepting an ask is the same invitation as any other - the request only
  // saved the owner typing the address.
  const inviteFromAsk = useMutation({
    mutationFn: async (ask: { id: string; requesterEmail: string }) => {
      const result = await sendInvitation(ask.requesterEmail);
      await dismissVisibilityRequest(ask.id);
      return result;
    },
    onSuccess: (result) => {
      setLastLink(result.link);
      void queryClient.invalidateQueries({ queryKey: ["visibility-requests"] });
      return invalidate();
    },
  });

  const all = invitations.data?.invitations ?? [];
  const visible = filter === "all" ? all : all.filter((entry) => entry.status === filter);

  const pending = asks.data?.requests ?? [];

  const asked = (iso: string): string => {
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 864e5);
    if (days === 0) return "Asked today";
    if (days === 1) return "Asked yesterday";
    return `Asked ${String(days)} days ago`;
  };

  const ago = (iso: string | null): string => {
    if (!iso) return "never";
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 864e5);
    if (days === 0) return "today";
    if (days === 1) return "yesterday";
    return `${String(days)} days ago`;
  };

  return (
    <section className="card wide">
      <h1>Who has access</h1>
      <p className="lede">
        Only people you invite can see when you&rsquo;re available — never what
        you&rsquo;re doing. Each invitation is tied to one email address; revoke it any
        time.
      </p>

      {pending.length > 0 ? (
        <div className="asks">
          <h2>Asked for access</h2>
          <p className="helper">
            They typed your name or address into the Meet With landing page. They
            can&rsquo;t see anything unless you invite them, and they&rsquo;re never told
            either way.
          </p>
          {pending.map((ask) => (
            <div key={ask.id} className="account-head">
              <div className="account-id">
                <strong>{ask.requesterEmail}</strong>
                <span className="mono">{ask.note ?? "No reason given"}</span>
              </div>
              <span className="count">{asked(ask.createdAt)}</span>
              <button
                type="button"
                className="secondary"
                onClick={() => inviteFromAsk.mutate(ask)}
                disabled={inviteFromAsk.isPending}
              >
                Invite
              </button>
              <button
                type="button"
                className="ghost"
                onClick={() => dismissAsk.mutate(ask.id)}
                disabled={dismissAsk.isPending}
              >
                Ignore
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="row">
        {(["all", "confirmed", "invited", "revoked"] as const).map((key) => {
          const count =
            key === "all"
              ? all.length
              : all.filter((entry) => entry.status === key).length;
          return (
            <button
              key={key}
              type="button"
              className={filter === key ? "chip chip-on" : "chip"}
              aria-pressed={filter === key}
              onClick={() => setFilter(key)}
            >
              {key === "all" ? "All" : STATUS_LABEL[key]} · {count}
            </button>
          );
        })}
      </div>

      {visible.map((invitation) => (
        <div key={invitation.id} className="account-head">
          <div className="account-id">
            <strong>{invitation.visitorName ?? invitation.email}</strong>
            <span className="mono">
              {invitation.visitorName
                ? invitation.email
                : "Name appears once they confirm"}
            </span>
          </div>
          <span
            className={
              invitation.status === "confirmed" ? "status" : "status status-warn"
            }
          >
            {STATUS_LABEL[invitation.status]}
          </span>
          <span className="count">Last active {ago(invitation.lastActiveAt)}</span>
          {invitation.status === "revoked" ? (
            <button
              type="button"
              className="ghost"
              onClick={() => setEmail(invitation.email)}
            >
              Re-invite
            </button>
          ) : (
            <button
              type="button"
              className="ghost"
              onClick={() => revoke.mutate(invitation.id)}
              disabled={revoke.isPending}
            >
              Revoke
            </button>
          )}
        </div>
      ))}

      {!invitations.isPending && all.length === 0 ? (
        <p className="helper">
          Nobody yet. Until you invite someone, nobody can see your availability.
        </p>
      ) : null}

      <h2>Invite someone</h2>
      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          invite.mutate();
        }}
      >
        <input
          type="email"
          required
          placeholder="name@company.example"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <input
          type="text"
          placeholder="Optional note included in the email"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <button type="submit" className="primary" disabled={invite.isPending}>
          {invite.isPending ? "Sending…" : "Send invitation"}
        </button>
      </form>
      <p className="helper">
        Sent from Meet With, not your mailbox. The link works only for that address.
      </p>

      {invite.isError ? (
        <div className="notice notice-error" role="alert">
          <strong>That didn&rsquo;t send</strong>
          <span>{invite.error.message}</span>
        </div>
      ) : null}

      {lastLink ? (
        <div className="notice" role="status">
          <strong>Invitation sent</strong>
          <span>
            If their mail is slow you can pass this link on another way. It is shown once.
          </span>
          <textarea className="request" readOnly rows={2} value={lastLink} />
          <div className="row">
            <button
              type="button"
              className="ghost"
              onClick={() => void navigator.clipboard?.writeText(lastLink)}
            >
              Copy link
            </button>
            <button type="button" className="ghost" onClick={() => setLastLink(null)}>
              Dismiss
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
